import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import NotificationsBell from './NotificationsBell';
import PresenterButton from './PresenterButton';import {
  Truck, LayoutDashboard, Package, MapPin, Wifi, Bell,
  Users, FileText, LogOut, ChevronLeft, ChevronRight, Route, Thermometer,
  Sun, Moon, Tag, Zap, Shield, Snowflake, Sparkles, Menu, X, QrCode,
  Building2, UserRound, Briefcase
} from 'lucide-react';

export default function OpsLayout() {
  const [collapsed, setCollapsed] = useState(false);     // desktop collapse
  const [mobileOpen, setMobileOpen] = useState(false);   // mobile drawer
  const { user, tenant, logout, loading, hasPermission } = useTrackOticAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (!loading && !user) {
      navigate('/ops/login');
    }
     
  }, [user, loading, navigate]);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 dark:bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/ops/login');
  };

  // Profile-based sidebar. Bank tenants don't ship parcels through a cold chain,
  // so hide Cold Chain / HUB Cold Room / Label Inventory / Passport Chain
  // from CIT profiles (BANK, BANK_P2). Track0tic Control admins see everything.
  // Fallback to tenant.code when profile_type isn't serialised by /auth/me on
  // older backends — defence in depth against schema drift.
  const profile = tenant?.profile_type;
  const bankProfiles = ['BANK', 'BANK_P2'];
  const bankCodes = ['AXIS', 'HDFC'];
  const isBankProfile = bankProfiles.includes(profile) || bankCodes.includes(tenant?.code);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/ops/dashboard', permission: null },
    { id: 'onboarding', label: 'Onboarding', icon: Sparkles, path: '/ops/onboarding', permission: null, coldChainOnly: true },
    { id: 'dispatcher', label: 'Dispatcher', icon: Zap, path: '/ops/dispatcher', permission: null, coldChainOnly: true },
    { id: 'delivery', label: 'Deliveries', icon: Truck, path: '/ops/delivery', permission: null, coldChainOnly: true },
    // Phase 36a — CIT portal entries (only shown for BANK_P2 tenants)
    { id: 'vans', label: 'Vans', icon: Truck, path: '/ops/vans', permission: null, bankOnly: true },
    { id: 'cit-vendors', label: 'CIT Vendors', icon: Briefcase, path: '/ops/cit-vendors', permission: null, bankOnly: true },
    { id: 'drivers', label: 'Drivers', icon: UserRound, path: '/ops/drivers', permission: null, bankOnly: true },
    { id: 'work-orders', label: isBankProfile ? 'CIT Runs' : 'Work Orders', icon: Package, path: '/ops/work-orders', permission: null },
    { id: 'trips', label: isBankProfile ? 'Van Trips' : 'Trips & Invoices', icon: Route, path: '/ops/trips', permission: null },
    { id: 'tracking', label: 'Live Tracking', icon: MapPin, path: '/ops/tracking', permission: null },
    { id: 'cold-chain', label: 'Cold Chain', icon: Thermometer, path: '/ops/cold-chain', permission: null, coldChainOnly: true },
    { id: 'labels', label: 'Label Inventory', icon: Tag, path: '/ops/labels', permission: null, coldChainOnly: true },
    { id: 'passport', label: 'Passport Chain', icon: Shield, path: '/ops/passport', permission: null, coldChainOnly: true },
    { id: 'passport-stock', label: 'Passport Stock', icon: Tag, path: '/ops/passport-stock', permission: null },
    { id: 'qr-ops', label: 'QR Operations', icon: QrCode, path: '/ops/qr-ops', permission: null },
    { id: 'hub', label: 'HUB Cold Room', icon: Snowflake, path: '/ops/hub', permission: null, coldChainOnly: true },
    { id: 'devices', label: isBankProfile ? 'Van Health' : 'Device Health', icon: Wifi, path: '/ops/devices', permission: null },
    { id: 'alerts', label: 'Alerts', icon: Bell, path: '/ops/alerts', permission: null },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/ops/reports', permission: null },
    { id: 'users', label: 'Users', icon: Users, path: '/ops/users', permission: null },
  ]
    .filter(item => !item.permission || hasPermission(item.permission))
    .filter(item => !(item.coldChainOnly && isBankProfile))
    .filter(item => !(item.bankOnly && !isBankProfile));

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo header */}
      <div className={cn("h-16 flex items-center justify-between px-4 border-b shrink-0",
        isDark ? "border-slate-800" : "border-slate-200")}>
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: tenant?.primary_color || '#0d9488' }}
            >
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className={cn("font-bold truncate", isDark ? "text-white" : "text-slate-900")}>{tenant?.name || 'Track0tic'}</span>
          </div>
        )}
        {collapsed && !isMobile && (
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto"
            style={{ backgroundColor: tenant?.primary_color || '#0d9488' }}
          >
            <Truck className="w-5 h-5 text-white" />
          </div>
        )}
        {isMobile ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(false)}
            className={cn(isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}
            data-testid="ops-mobile-close"
          >
            <X className="w-5 h-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={cn(isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}
            data-testid="ops-sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path ||
            (item.path !== '/ops/dashboard' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                isActive
                  ? "text-white"
                  : isDark
                    ? "text-slate-400 hover:text-white hover:bg-slate-800"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              )}
              style={isActive ? { backgroundColor: tenant?.primary_color || '#0d9488' } : {}}
              data-testid={`ops-nav-${item.id}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {(!collapsed || isMobile) && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer (user + theme + logout) */}
      <div className={cn("p-4 border-t shrink-0", isDark ? "border-slate-800" : "border-slate-200")}>
        {(!collapsed || isMobile) ? (
          <div className="mb-3 flex items-center justify-between">
            <div className="min-w-0">
              <p className={cn("font-medium truncate", isDark ? "text-white" : "text-slate-900")}>{user?.full_name}</p>
              <p className={cn("text-sm truncate", isDark ? "text-slate-500" : "text-slate-400")}>{tenant?.name}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn("h-8 w-8 shrink-0", isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100")}
              data-testid="ops-theme-toggle-btn"
            >
              {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className={cn("w-full mb-2", isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}
            data-testid="ops-theme-toggle-btn-collapsed"
          >
            {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          className={cn(
            isDark
              ? "text-slate-400 hover:text-white hover:bg-slate-800"
              : "text-slate-500 hover:text-red-600 hover:bg-red-50",
            (collapsed && !isMobile) ? "w-full justify-center" : "w-full justify-start"
          )}
          onClick={handleLogout}
          data-testid="ops-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          {(!collapsed || isMobile) && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </>
  );

  return (
    <div className={cn("min-h-screen flex", isDark ? "bg-slate-950" : "bg-slate-100")}>
      {/* Desktop Sidebar */}
      <aside className={cn(
        "border-r flex flex-col transition-all duration-300 hidden md:flex",
        isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200",
        collapsed ? "w-16" : "w-64"
      )}>
        <SidebarContent />
      </aside>

      {/* Mobile Drawer (overlay) */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          data-testid="ops-mobile-backdrop"
        />
      )}
      <aside
        aria-hidden={!mobileOpen}
        className={cn(
        "md:hidden fixed top-0 left-0 bottom-0 z-50 w-72 flex flex-col border-r transition-transform duration-300",
        isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <SidebarContent isMobile />
      </aside>

      {/* Main */}
      <main className={cn("flex-1 overflow-auto min-w-0 relative", isDark ? "bg-slate-950" : "bg-slate-100")}>
        <NotificationsBell />
        <PresenterButton />
        <div className="pr-24 md:pr-28">
        {/* Mobile top bar */}
        <div className={cn(
          "md:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b",
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"
        )}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            className={cn(isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900")}
            data-testid="ops-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: tenant?.primary_color || '#0d9488' }}>
              <Truck className="w-4 h-4 text-white" />
            </div>
            <span className={cn("font-bold truncate text-sm", isDark ? "text-white" : "text-slate-900")}>
              {tenant?.name || 'Track0tic'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className={cn("h-9 w-9", isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900")}
            data-testid="ops-mobile-theme-btn"
          >
            {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>
        <Outlet />
        </div>
      </main>
    </div>
  );
}
